import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ArrowUturnLeftIcon,
  ClockIcon,
  ExclamationCircleIcon,
  RectangleGroupIcon,
} from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import { useSearchParams } from "react-router-dom";
import Dropdown from "../forms/Dropdown";
import Filters from "../layout/Filters";
import Card from "../layout/Card";
import SwitchToggle from "../forms/SwitchToggle";
import DangerZone from "../forms/DangerZone";
import Indicator from "../layout/Indicator";
import { getSolidIcon } from "../../utils/icons";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";
import {
  TaskState,
  createTask,
  deleteTasks,
  getTasks,
  updateTask,
} from "../../slices/tasks";
import { getRooms } from "../../slices/rooms";
import IntegerInput from "../forms/IntegerInput";
import HorizontalRadio from "../forms/HorizontalRadio";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import Checkbox from "../forms/Checkbox";
import DateSelector from "../forms/DateSelector";
import TimeSelector from "../forms/TimeSelector";

export default function Manage() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const searchCategory = searchParams.get("category");
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(
      getTasks({
        category: searchCategory,
        task: null,
        dateRange: dateRangeTerm,
      }),
    );
    dispatch(getRooms());
  }, [dateRangeTerm, dispatch, searchCategory]);

  // Set state
  const [category, setCategory] = useState({ value: "" });
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [selectedTask, setSelectedTask] = useState<{
    id: string;
    name: { [key: string]: string };
    description: { [key: string]: string };
    category: string;
    frequency_choice: string;
    frequency_value: number;
    target: string;
    choose_rooms_by: string;
    rooms: string[];
    tags: string[];
    status: string;
  }>({
    id: "",
    name: { EN: "", FR: "", IT: "", DE: "" },
    description: { EN: "", FR: "", IT: "", DE: "" },
    category: "CLN",
    frequency_choice: "DAY",
    frequency_value: 1,
    target: "A",
    choose_rooms_by: "name",
    rooms: [],
    tags: [],
    status: "A",
  });
  const [createTaskForm, setCreateTaskForm] = useState<{
    name: { [key: string]: string };
    description: { [key: string]: string };
    category: string;
    frequency_choice: string;
    frequency_value: number;
    target: string;
    choose_rooms_by: string;
    rooms: string[];
    tags: string[];
    status: string;
  }>({
    name: { EN: "", FR: "", IT: "", DE: "" },
    description: { EN: "", FR: "", IT: "", DE: "" },
    category: "CLN",
    frequency_choice: "DAY",
    frequency_value: 1,
    target: "A",
    choose_rooms_by: "name",
    rooms: [],
    tags: [],
    status: "A",
  });
  const [createTaskStartDate, setCreateTaskStartDate] = useState<any>({
    startDate: null,
    endDate: null,
  });
  const [createTaskStartTime, setCreateTaskStartTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "0", minute: "0" });
  const [editTaskStartDate, setEditTaskStartDate] = useState<any>({
    startDate: null,
    endDate: null,
  });
  const [editTaskStartTime, setEditTaskStartTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { tasks, options } = useAppSelector((state) => state.tasks);
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectTaskToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [taskId] = args;
    const taskData = tasksData.filter((task) => task.id === taskId)[0];
    setSelectedTask({
      id: taskData.id,
      name: taskData.name,
      description: taskData.description,
      category: taskData.category[0],
      frequency_choice: taskData.frequency_choice[0],
      frequency_value: taskData.frequency_value,
      target: taskData.target[0],
      choose_rooms_by: "name",
      rooms: taskData.rooms,
      tags: [],
      status: taskData.status[0],
    });
    setEditTaskStartDate({
      startDate: taskData.start_date.string,
      endDate: taskData.start_date.string,
    });
    setEditTaskStartTime({
      hour: parseInt(taskData.start_time.split(":")[0]).toString(),
      minute: parseInt(taskData.start_time.split(":")[1]).toString(),
    });
    setShowEditModal(true);
  }

  function openCreateTaskModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function selectOrUnselectAllCreateRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setCreateTaskForm({ ...createTaskForm, rooms: allAvailableRooms });
    } else {
      setCreateTaskForm({ ...createTaskForm, rooms: [] });
    }
  }

  function selectOrUnselectAllCreateTags(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableTags = roomTags.map((x: any) => x.id);
      setCreateTaskForm({ ...createTaskForm, tags: allAvailableTags });
    } else {
      setCreateTaskForm({ ...createTaskForm, tags: [] });
    }
  }

  function updateRoomForCreateEvent(roomId: string) {
    if (createTaskForm.rooms.includes(roomId)) {
      const updatedRooms = createTaskForm.rooms.filter((x) => x !== roomId);
      setCreateTaskForm({ ...createTaskForm, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createTaskForm.rooms, roomId];
      setCreateTaskForm({ ...createTaskForm, rooms: updatedRooms });
    }
  }

  function updateTagForCreateEvent(tag: string) {
    const existingTags = createTaskForm.tags;
    if (existingTags.includes(tag)) {
      const updatedTags = existingTags.filter((x) => x !== tag);
      setCreateTaskForm({ ...createTaskForm, tags: updatedTags });
    } else {
      const updatedTags = [...existingTags, tag];
      setCreateTaskForm({ ...createTaskForm, tags: updatedTags });
    }
  }

  function selectOrUnselectAllEditRooms(e: any, args: any[]) {
    e.preventDefault();
    if (selectedTask === null) return;
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setSelectedTask({ ...selectedTask, rooms: allAvailableRooms });
    } else {
      setSelectedTask({ ...selectedTask, rooms: [] });
    }
  }

  function selectOrUnselectAllEditTags(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableTags = roomTags.map((x: any) => x.id);
      setSelectedTask({ ...selectedTask, tags: allAvailableTags });
    } else {
      setSelectedTask({ ...selectedTask, tags: [] });
    }
  }

  function updateRoomForEditEvent(roomId: string) {
    if (selectedTask === null) return;
    if (selectedTask.rooms.includes(roomId)) {
      const updatedRooms = selectedTask.rooms.filter((x) => x !== roomId);
      setSelectedTask({ ...selectedTask, rooms: updatedRooms });
    } else {
      const updatedRooms = [...selectedTask.rooms, roomId];
      setSelectedTask({ ...selectedTask, rooms: updatedRooms });
    }
  }

  function updateTagForEditEvent(tag: string) {
    if (selectedTask === null) return;
    const selectedEventTags = selectedTask.tags;
    if (selectedEventTags.includes(tag)) {
      const updatedTags = selectedEventTags.filter((x) => x !== tag);
      setSelectedTask({ ...selectedTask, tags: updatedTags });
    } else {
      const updatedTags = [...selectedEventTags, tag];
      setSelectedTask({ ...selectedTask, tags: updatedTags });
    }
  }

  async function confirmCreateTask(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      name,
      description,
      category,
      frequency_choice: frequencyChoice,
      frequency_value: frequencyValue,
      target,
      choose_rooms_by: chooseRoomsBy,
      rooms,
      tags,
      status,
    } = createTaskForm;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (category === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_category", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    // Validate rooms
    let chosenRooms: string[] = [];
    if (target === "S") {
      if (chooseRoomsBy === "name") {
        chosenRooms = rooms;
      } else {
        chosenRooms = roomsData
          .filter((room) => tags.includes(room.tag[0]))
          .map((room) => room.id);
      }
      if (chosenRooms.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_rooms", language),
            "danger",
            5000,
          ),
        );
        return;
      }
    }

    // Validate start date
    const startDate = createTaskStartDate.startDate;
    if (startDate === null) {
      dispatch(
        addAlert(
          generateAlertText("missing_start_date", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    // Validate start time
    const startTime = createTaskStartTime;
    if (startTime.hour === "" || startTime.minute === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_start_time", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    const startTimeFormatted = `${startTime.hour}:${startTime.minute}:00`;

    setShowCreateModal(false);
    const res: any = await dispatch(
      createTask({
        name,
        description,
        property: user.property.id,
        category,
        frequencyChoice,
        frequencyValue,
        startDate,
        startTime: startTimeFormatted,
        target,
        rooms: chosenRooms,
        status,
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(generateAlertText("create_task", language), "success", 3000),
      );
      setCreateTaskForm({
        name: { EN: "", FR: "", IT: "", DE: "" },
        description: { EN: "", FR: "", IT: "", DE: "" },
        category: "CLN",
        frequency_choice: "DAY",
        frequency_value: 1,
        target: "A",
        choose_rooms_by: "name",
        rooms: [],
        tags: [],
        status: "A",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditTask(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      id,
      name,
      description,
      category,
      frequency_choice: frequencyChoice,
      frequency_value: frequencyValue,
      target,
      choose_rooms_by: chooseRoomsBy,
      rooms,
      tags,
      status,
    } = selectedTask;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (category === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_category", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    // Validate rooms
    let chosenRooms: string[] = [];
    if (target === "S") {
      if (chooseRoomsBy === "name") {
        chosenRooms = rooms;
      } else {
        chosenRooms = roomsData
          .filter((room) => tags.includes(room.tag[0]))
          .map((room) => room.id);
      }
      if (chosenRooms.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_rooms", language),
            "danger",
            5000,
          ),
        );
        return;
      }
    }

    // Validate start date
    const startDate = editTaskStartDate.startDate;
    if (startDate === null) {
      dispatch(
        addAlert(
          generateAlertText("missing_start_date", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    // Validate start time
    const startTime = editTaskStartTime;
    if (startTime.hour === "" || startTime.minute === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_start_time", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    const startTimeFormatted = `${startTime.hour}:${startTime.minute}:00`;

    setShowEditModal(false);
    const res: any = await dispatch(
      updateTask({
        taskId: id,
        name,
        description,
        property: user.property.id,
        category,
        frequencyChoice,
        frequencyValue,
        startDate,
        startTime: startTimeFormatted,
        target,
        rooms,
        status,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(generateAlertText("update_task", language), "success", 3000),
      );
      setSelectedTask({
        id: "",
        name: { EN: "", FR: "", IT: "", DE: "" },
        description: { EN: "", FR: "", IT: "", DE: "" },
        category: "CLN",
        frequency_choice: "DAY",
        frequency_value: 1,
        target: "A",
        choose_rooms_by: "name",
        rooms: [],
        tags: [],
        status: "A",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeleteTask(e: any, args: any[]) {
    e.preventDefault();
    const [taskIds] = args;
    if (taskIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (taskIds === null || taskIds === undefined) {
      dispatch(
        addAlert(generateAlertText("missing_tasks", language), "danger", 3000),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(deleteTasks({ taskIds: [taskIds] }));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function getOutstandingRoomsText(numRooms: number) {
    if (numRooms === 1)
      return `${numRooms} ${translateText("room_remaining", language)}`;
    return `${numRooms} ${translateText("rooms_remaining", language)}`;
  }

  function getCardBgColour(
    complete: boolean,
    numRoomsOutstanding: number,
    totalRooms: number,
  ) {
    if (complete) return "success";
    const pctComplete =
      numRoomsOutstanding === 0
        ? 0
        : 100 - (numRoomsOutstanding / totalRooms) * 100;
    if (pctComplete < 33) return "danger";
    if (pctComplete < 66) return "warning";
    return "white";
  }

  function getCardTextColour(
    complete: boolean,
    numRoomsOutstanding: number,
    totalRooms: number,
  ) {
    if (complete) return "white";
    const pctComplete =
      numRoomsOutstanding === 0
        ? 0
        : 100 - (numRoomsOutstanding / totalRooms) * 100;
    if (pctComplete < 33) return "white";
    if (pctComplete < 66) return "black";
    return "black";
  }

  // Declare constants to be used
  const tasksData = tasks.data === null ? [] : tasks.data;
  const tasksCategories =
    options.category === undefined
      ? []
      : Object.keys(options.category).map((key) => ({
          value: key,
          label: options.category[key][language],
        }));
  const tasksCategoriesChoices =
    options.category === undefined
      ? []
      : Object.keys(options.category).map((key) => [
          key,
          options.category[key][language],
        ]);
  const frequencyChoiceChoices =
    options.frequency_choice === undefined
      ? []
      : Object.keys(options.frequency_choice).map((key) => [
          key,
          options.frequency_choice[key][language],
        ]);
  const roomsData = rooms.data === null ? [] : rooms.data;
  let roomTags: any = [];
  for (let i = 0; i < roomsData.length; i++) {
    const candidateTags = roomsData[i].tag;
    for (let j = 0; j < candidateTags.length; j++) {
      if (!roomTags.includes(candidateTags[j])) {
        roomTags.push(candidateTags[j]);
      }
    }
  }
  roomTags.sort();
  roomTags = roomTags.map((x: any) => ({ id: x, label: x }));
  const chooseRoomsBy = [
    ["name", translateText("name", language)],
    ["tag", translateText("tag_single", language)],
  ];
  const targetTypes = [
    ["A", translateText("all", language)],
    ["S", translateText("specific", language)],
  ];

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("tasks_manage_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateTaskModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_task", language)}
              </Button>
            }
          />
        }
      />

      {/* Create task modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_task", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateTask}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_task", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createTaskForm}
                stateName="name"
                onChange={setCreateTaskForm}
                defaultLanguage={language}
                languageChoices={
                  tasksData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(tasksData[0].name)
                }
              />

              {/* Description */}
              <TextInputMultiLanguage
                id="description"
                type="text"
                label={translateText("description", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createTaskForm}
                stateName="description"
                onChange={setCreateTaskForm}
                defaultLanguage={language}
                languageChoices={
                  tasksData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(tasksData[0].description)
                }
              />

              {/* Category */}
              <Dropdown
                id="category"
                className="col-span-full"
                label={translateText("category", language)}
                showLabel={true}
                required={true}
                state={createTaskForm}
                stateName="category"
                onChange={setCreateTaskForm}
                defaultValue="CLN"
              >
                {tasksCategoriesChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Frequency choice */}
              <Dropdown
                id="category"
                className="col-span-full"
                label={translateText("task_frequency_choice", language)}
                showLabel={true}
                required={true}
                state={createTaskForm}
                stateName="frequency_choice"
                onChange={setCreateTaskForm}
                defaultValue="DAY"
              >
                {frequencyChoiceChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Frequency value */}
              <IntegerInput
                id="frequency_value"
                min={1}
                max={1000}
                label={translateText("task_frequency_value", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createTaskForm}
                stateName="frequency_value"
                onChange={setCreateTaskForm}
              />

              {/* Start date */}
              <DateSelector
                label={translateText("start_date", language)}
                showLabel={true}
                dateState={createTaskStartDate}
                dateHandler={setCreateTaskStartDate}
                language={language}
              />

              {/* Start time */}
              <TimeSelector
                label={translateText("start_time", language)}
                showLabel={true}
                timeState={createTaskStartTime}
                timeHandler={setCreateTaskStartTime}
                language={language}
              />

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_task_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={createTaskForm.target === choice[0]}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreateTaskForm({
                            ...createTaskForm,
                            target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {createTaskForm.target === "S" && (
                <>
                  {/* Choose rooms by */}
                  <Dropdown
                    id="choose_rooms_by"
                    className="col-span-full"
                    label={translateText("choose_rooms_by", language)}
                    small={translateText("choose_rooms_by_subtext", language)}
                    showLabel={true}
                    required={true}
                    state={createTaskForm}
                    stateName="choose_rooms_by"
                    onChange={setCreateTaskForm}
                    defaultValue="name"
                  >
                    {chooseRoomsBy.map((opt) => (
                      <option key={opt[0]} value={opt[0]}>
                        {opt[1]}
                      </option>
                    ))}
                  </Dropdown>

                  {/* Rooms */}
                  {createTaskForm.choose_rooms_by === "name" ? (
                    <>
                      <div>
                        <label className="mb-1 block text-left text-sm font-medium text-black">
                          {translateText("rooms", language)}
                        </label>
                        <p className="text-left text-xs italic text-secondary">
                          {translateText("rooms_template_subtext", language)}
                        </p>
                      </div>

                      {/* Select/unselect all buttons */}
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllCreateRooms}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomsData.map((room: any) => (
                          <Checkbox
                            key={room.id}
                            id={room.id}
                            onChange={updateRoomForCreateEvent}
                            checked={createTaskForm.rooms.includes(room.id)}
                            label={room.name}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="mb-1 block text-left text-sm font-medium text-black">
                          {translateText("tag", language)}
                        </label>
                        <p className="text-left text-xs italic text-secondary">
                          {translateText(
                            "select_tags_for_rooms_campaign",
                            language,
                          )}
                        </p>
                      </div>

                      {/* Select/unselect all buttons */}
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllCreateTags}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomTags.map((tag: any) => (
                          <Checkbox
                            key={tag.id}
                            id={tag.id}
                            onChange={updateTagForCreateEvent}
                            checked={createTaskForm.tags.includes(tag.id)}
                            label={tag.label}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Left aligned text explaining current freqeuency choices */}
              {/* <div className="col-span-full">
                <span className="text-slate-600 text-xs">
                  {`Every ${createTaskForm.frequency_value} ${frequencyChoiceChoices.filter((x) => x[0] === createTaskForm.frequency_choice)[0][1]}`}
                </span>
              </div> */}
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit task modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_task", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditTask}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_task", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedTask}
                stateName="name"
                onChange={setSelectedTask}
                defaultLanguage={language}
                languageChoices={
                  tasksData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(tasksData[0].name)
                }
              />

              {/* Description */}
              <TextInputMultiLanguage
                id="description"
                type="text"
                label={translateText("description", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedTask}
                stateName="description"
                onChange={setSelectedTask}
                defaultLanguage={language}
                languageChoices={
                  tasksData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(tasksData[0].description)
                }
              />

              {/* Category */}
              <Dropdown
                id="category"
                className="col-span-full"
                label={translateText("type", language)}
                showLabel={true}
                required={true}
                state={selectedTask}
                stateName="category"
                onChange={setSelectedTask}
                defaultValue={selectedTask.category}
              >
                {tasksCategoriesChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Frequency choice */}
              <Dropdown
                id="category"
                className="col-span-full"
                label={translateText("task_frequency_choice", language)}
                showLabel={true}
                required={true}
                state={selectedTask}
                stateName="frequency_choice"
                onChange={setSelectedTask}
                defaultValue={selectedTask.frequency_choice}
              >
                {frequencyChoiceChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Frequency value */}
              <IntegerInput
                id="frequency_value"
                min={1}
                max={1000}
                label={translateText("task_frequency_value", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedTask}
                stateName="frequency_value"
                onChange={setSelectedTask}
              />

              {/* Start date */}
              <DateSelector
                label={translateText("start_date", language)}
                showLabel={true}
                dateState={editTaskStartDate}
                dateHandler={setEditTaskStartDate}
                language={language}
              />

              {/* Start time */}
              <TimeSelector
                label={translateText("start_time", language)}
                showLabel={true}
                timeState={editTaskStartTime}
                timeHandler={setEditTaskStartTime}
                language={language}
              />

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_task_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={selectedTask.target === choice[0]}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setSelectedTask({
                            ...selectedTask,
                            target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {selectedTask.target === "S" && (
                <>
                  {/* Choose rooms by */}
                  <Dropdown
                    id="choose_rooms_by"
                    className="col-span-full"
                    label={translateText("choose_rooms_by", language)}
                    small={translateText("choose_rooms_by_subtext", language)}
                    showLabel={true}
                    required={true}
                    state={selectedTask}
                    stateName="choose_rooms_by"
                    onChange={setSelectedTask}
                    defaultValue="name"
                  >
                    {chooseRoomsBy.map((opt) => (
                      <option key={opt[0]} value={opt[0]}>
                        {opt[1]}
                      </option>
                    ))}
                  </Dropdown>

                  {/* Rooms */}
                  {selectedTask.choose_rooms_by === "name" ? (
                    <>
                      <div>
                        <label className="mb-1 block text-left text-sm font-medium text-black">
                          {translateText("rooms", language)}
                        </label>
                        <p className="text-left text-xs italic text-secondary">
                          {translateText("rooms_template_subtext", language)}
                        </p>
                      </div>

                      {/* Select/unselect all buttons */}
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllEditRooms}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomsData.map((room: any) => (
                          <Checkbox
                            key={room.id}
                            id={room.id}
                            onChange={updateRoomForEditEvent}
                            checked={selectedTask.rooms.includes(room.id)}
                            label={room.name}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="mb-1 block text-left text-sm font-medium text-black">
                          {translateText("tag", language)}
                        </label>
                        <p className="text-left text-xs italic text-secondary">
                          {translateText(
                            "select_tags_for_rooms_campaign",
                            language,
                          )}
                        </p>
                      </div>

                      {/* Select/unselect all buttons */}
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllEditTags}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomTags.map((tag: any) => (
                          <Checkbox
                            key={tag.id}
                            id={tag.id}
                            onChange={updateTagForEditEvent}
                            checked={selectedTask.tags.includes(tag.id)}
                            label={tag.label}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                description={translateText("status_task_subtext", language)}
                showLabel={true}
                toggle={selectedTask.status === "A"}
                state={selectedTask}
                stateName="status"
                handler={setSelectedTask}
                mapping={{ true: "A", false: "I" }}
              />

              {/* Danger zone */}
              <DangerZone
                objectId={selectedTask.id}
                handler={confirmDeleteTask}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {tasks.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "category",
                  name: "Category",
                  options: tasksCategories,
                },
              ]}
              queryParams={[
                ["category", searchCategory],
                ["dateRange", dateRangeTerm],
              ]}
              handlers={{
                category: {
                  state: category,
                  stateName: "value",
                  onChange: setCategory,
                },
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={tasks.loading}
              baseUrl="/tasks/manage"
              showDateRange={true}
              showFilters={true}
              showSearchBar={false}
            />
            {tasksData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {tasksData.map((task: TaskState) => {
                    const bgColour = getCardBgColour(
                      task.completion_status.completed,
                      task.completion_status.outstanding_rooms.length,
                      task.completion_status.task_rooms.length,
                    );
                    const textColour = getCardTextColour(
                      task.completion_status.completed,
                      task.completion_status.outstanding_rooms.length,
                      task.completion_status.task_rooms.length,
                    );
                    const subTextColour =
                      bgColour === "white" ? "secondary" : textColour;
                    const mainIconColour =
                      bgColour === "white"
                        ? "info"
                        : bgColour === "warning"
                        ? "black"
                        : "white";
                    return (
                      <a
                        href="#!"
                        key={task.id}
                        onClick={(e) => selectTaskToEdit(e, [task.id])}
                        className="relative"
                      >
                        {/* Task data */}
                        <Card
                          key={task.id}
                          colour={bgColour}
                          className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                        >
                          <div className={`border-b border-light pb-2`}>
                            <div className="items-center justify-between">
                              <div>
                                <h3
                                  className={`text-base font-semibold leading-6 text-${textColour} truncate`}
                                >
                                  {task.name[language]}
                                </h3>
                              </div>
                            </div>
                          </div>
                          <div className="pt-2 grid grid-cols-1 space-y-2">
                            {/* Category */}
                            <div
                              className={`inline-flex text-sm text-${subTextColour}`}
                            >
                              {getSolidIcon(
                                task.category[0],
                                mainIconColour,
                                5,
                                "mr-1",
                              )}
                              {task.category[1]}
                            </div>

                            {/* Period end */}
                            <div
                              className={`inline-flex text-sm text-${subTextColour}`}
                            >
                              <ClockIcon
                                className={`h-5 w-5 text-${subTextColour} mr-1`}
                                aria-hidden="true"
                              />
                              {task.time_until_period_end.string}
                            </div>

                            {/* Last logged */}
                            {task.last_logged !== null && (
                              <div
                                className={`inline-flex text-sm text-${subTextColour}`}
                              >
                                <ArrowUturnLeftIcon
                                  className={`h-5 w-5 text-${subTextColour} mr-1`}
                                  aria-hidden="true"
                                />
                                {task.last_logged}
                              </div>
                            )}

                            {/* Rooms outstanding */}
                            <div
                              className={`inline-flex text-sm text-${subTextColour}`}
                            >
                              <RectangleGroupIcon
                                className={`h-5 w-5 text-${subTextColour} mr-1`}
                                aria-hidden="true"
                              />
                              {getOutstandingRoomsText(
                                task.completion_status.outstanding_rooms.length,
                              )}
                            </div>
                          </div>
                        </Card>

                        {/* Status indicator */}
                        <Indicator
                          size={3}
                          colour={
                            task.status[0] === "A" ? "success" : "warning"
                          }
                          pulse={false}
                        />
                      </a>
                    );
                  })}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_tasks", language)}
                subtext={translateText("no_tasks_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
